import { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { SQUAD_COLORS } from './templateConstants';
import { SQUAD_ADVISOR_PHOTO } from './squadAdvisors';
import { SquadRichTextEditor } from './SquadRichTextEditor';

/** Split stored multiline text into editor rows (keeps blank draft rows). */
export const parseMultilineList = (text: string): string[] => {
  if (text == null || text === '') return [];
  return String(text)
    .split('\n')
    .map((line) => line.replace(/^[-–−+]\s*/, ''));
};

/** Persist editor rows; blank rows become empty lines so Add → empty field survives round-trip. */
export const joinMultilineList = (items: string[]): string => {
  if (!items?.length) return '';
  return items.map((item) => String(item ?? '')).join('\n');
};

/** Drop blank rows for preview/PDF rendering. */
export const nonemptyMultilineItems = (text: string): string[] =>
  parseMultilineList(text)
    .map((line) => line.trim())
    .filter(Boolean);

export const StringListEditor: FC<{
  items: string[];
  onChange: (items: string[]) => void;
  addLabel?: string;
  itemLabel?: string;
  marker?: string;
}> = ({ items: itemsProp, onChange, addLabel = 'Add item', itemLabel = 'Item', marker }) => {
  const [items, setItems] = useState<string[]>(() => itemsProp || []);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const propKey = (itemsProp || []).join('\n');

  // Sync from parent when non-empty content changes; keep local blank draft rows.
  useEffect(() => {
    const prop = propKey === '' ? [] : propKey.split('\n');
    const propFilled = prop.map((i) => i.trim()).filter(Boolean);
    const localFilled = itemsRef.current.map((i) => i.trim()).filter(Boolean);
    const sameFilled = JSON.stringify(propFilled) === JSON.stringify(localFilled);
    if (sameFilled && itemsRef.current.length >= prop.length) return;
    setItems(prop);
  }, [propKey]);

  const emit = (next: string[]) => {
    setItems(next);
    onChange(next);
  };

  return (
    <Stack spacing={1}>
      {items.map((item, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          {marker ? (
            <Typography
              component="span"
              sx={{ color: SQUAD_COLORS.navy, fontWeight: 700, pt: 1, minWidth: 16, lineHeight: 1.5 }}
            >
              {marker}
            </Typography>
          ) : null}
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={1}
            label={`${itemLabel} ${index + 1}`}
            value={item}
            onChange={(e) => {
              const next = [...items];
              next[index] = e.target.value;
              emit(next);
            }}
          />
          <IconButton
            size="small"
            type="button"
            aria-label={`Remove ${itemLabel} ${index + 1}`}
            onClick={() => emit(items.filter((_, i) => i !== index))}
            sx={{ mt: 0.5 }}
          >
            <Remove fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        type="button"
        size="small"
        startIcon={<Add />}
        onClick={() => emit([...(items || []), ''])}
        sx={{ alignSelf: 'flex-start', width: 'fit-content' }}
      >
        {addLabel}
      </Button>
    </Stack>
  );
};

export const PerformanceRiskTableEditor: FC<{
  value: { labels?: string[]; rows?: string[][] };
  onChange: (v: { labels: string[]; rows: string[][] }) => void;
}> = ({ value, onChange }) => {
  const labels = value?.labels || [];
  const rows = value?.rows || [];

  const updateCell = (ri: number, ci: number, cell: string) => {
    const nextRows = rows.map((r) => [...r]);
    nextRows[ri][ci] = cell;
    onChange({ labels, rows: nextRows });
  };

  const updateLabel = (ci: number, label: string) => {
    const nextLabels = [...labels];
    nextLabels[ci] = label;
    onChange({ labels: nextLabels, rows });
  };

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {labels.map((l, ci) => (
          <TextField
            key={`l-${ci}`}
            size="small"
            label={`Col ${ci}`}
            value={l}
            onChange={(e) => updateLabel(ci, e.target.value)}
            sx={{ width: ci === 0 ? 120 : 90 }}
          />
        ))}
      </Box>
      {rows.map((row, ri) => (
        <Box key={ri} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {row.map((cell, ci) => (
            <TextField
              key={`${ri}-${ci}`}
              size="small"
              label={ci === 0 ? `Row ${ri + 1}` : undefined}
              value={cell}
              onChange={(e) => updateCell(ri, ci, e.target.value)}
              sx={{ width: ci === 0 ? 120 : 90 }}
            />
          ))}
        </Box>
      ))}
    </Stack>
  );
};

export const KeyValueListEditor: FC<{
  items: Array<{ label: string; value: string }>;
  onChange: (items: Array<{ label: string; value: string }>) => void;
}> = ({ items, onChange }) => (
  <Stack spacing={1}>
    {(items || []).map((item, index) => (
      <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Label"
          value={item.label}
          onChange={(e) => {
            const next = [...items];
            next[index] = { ...next[index], label: e.target.value };
            onChange(next);
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="Value"
          value={item.value}
          onChange={(e) => {
            const next = [...items];
            next[index] = { ...next[index], value: e.target.value };
            onChange(next);
          }}
          sx={{ width: 120 }}
        />
        <IconButton size="small" onClick={() => onChange(items.filter((_, i) => i !== index))}>
          <Remove fontSize="small" />
        </IconButton>
      </Box>
    ))}
    <Button
      type="button"
      size="small"
      startIcon={<Add />}
      onClick={() => onChange([...(items || []), { label: '', value: '' }])}
      sx={{ alignSelf: 'flex-start', width: 'fit-content' }}
    >
      Add row
    </Button>
  </Stack>
);

export const AdvisorsEditor: FC<{
  advisors: Array<{
    name: string;
    company?: string;
    companyLine1?: string;
    companyLine2?: string;
    bio?: string;
    photoUrl?: string;
    logoUrl?: string;
    layout?: 'featured' | 'duo';
    duoGroup?: string;
  }>;
  onChange: (advisors: any[]) => void;
  onUploadPhoto: (index: number, url: string) => void;
  onUploadLogo: (index: number, url: string) => void;
  uploadField: FC<{
    label: string;
    value?: string;
    onChange: (url: string) => void;
    helperText?: string;
    previewSx?: Record<string, unknown>;
  }>;
}> = ({ advisors, onChange, onUploadPhoto, onUploadLogo, uploadField: UploadField }) => {
  const a = advisors?.[0] || { name: '', company: '', bio: '', photoUrl: '', logoUrl: '' };

  const patch = (partial: Partial<typeof a>) => {
    onChange([{ ...a, layout: 'featured', ...partial }]);
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={600}>
        Fondsberater
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Name"
        value={a.name}
        onChange={(e) => patch({ name: e.target.value })}
      />
      <TextField
        size="small"
        fullWidth
        label="Company"
        value={a.company || ''}
        onChange={(e) => patch({ company: e.target.value })}
      />
      <TextField
        size="small"
        fullWidth
        label="Company line 1 (logo text)"
        value={a.companyLine1 || ''}
        onChange={(e) => patch({ companyLine1: e.target.value })}
      />
      <TextField
        size="small"
        fullWidth
        label="Company line 2 (logo text)"
        value={a.companyLine2 || ''}
        onChange={(e) => patch({ companyLine2: e.target.value })}
      />
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Bio
        </Typography>
        {/* Justified block text with bold and hand-placed hyphenation — the four
            things SQUAD listed for this block in their layout document. */}
        <SquadRichTextEditor
          value={a.bio || ''}
          onChange={(html) => patch({ bio: html })}
          minHeight={120}
          enableSoftHyphen
        />
      </Box>
      <UploadField
        label="Photo"
        value={a.photoUrl}
        onChange={(url) => onUploadPhoto(0, url)}
        helperText={`Fixed size ${SQUAD_ADVISOR_PHOTO.heightPx} × ${SQUAD_ADVISOR_PHOTO.widthPx} px (H × B)`}
        previewSx={{
          width: '100%',
          maxWidth: SQUAD_ADVISOR_PHOTO.widthPx,
          aspectRatio: `${SQUAD_ADVISOR_PHOTO.widthPx} / ${SQUAD_ADVISOR_PHOTO.heightPx}`,
          height: 'auto',
          maxHeight: 'none',
          objectFit: 'contain',
          objectPosition: 'center',
          display: 'block',
          mb: 1,
          bgcolor: '#f3f4f6',
        }}
      />
      <UploadField label="Company logo" value={a.logoUrl} onChange={(url) => onUploadLogo(0, url)} />
    </Stack>
  );
};

export const SectionHeadersEditor: FC<{
  headers: Record<string, string>;
  onChange: (headers: Record<string, string>) => void;
}> = ({ headers, onChange }) => (
  <Stack spacing={1}>
    {Object.entries(headers).map(([key, label]) => (
      <TextField
        key={key}
        size="small"
        fullWidth
        label={key}
        value={label}
        onChange={(e) => onChange({ ...headers, [key]: e.target.value })}
      />
    ))}
  </Stack>
);
